import { RequiredCaption, RequiredMark } from '@/shared/ui/atoms/required-mark'
import type {
  InputNumberProps as AntInputNumberProps,
  InputProps as AntInputProps,
  InputRef as AntInputRef,
} from 'antd'
import { Input as AntInput, InputNumber as AntInputNumber, Space } from 'antd'
import { forwardRef, useId, type ReactNode } from 'react'
import './input.css'

type AntInputNumberRef = React.ComponentRef<typeof AntInputNumber>

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------
interface BaseFieldProps {
  label?: string | null
  width?: string
  height?: string
  status?: string
  required?: boolean
  errorData?: Record<string, string | null | undefined>
  field?: string
  style?: React.CSSProperties
  name?: string
  className?: string
  id?: string
  autoComplete?: string
  'aria-label'?: string
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------
export interface InputProps extends BaseFieldProps {
  type?: string
  placeholder?: string
  value?: string | number | null
  onChange?: AntInputProps['onChange']
  suffix?: ReactNode
  prefix?: ReactNode
  onPressEnter?: AntInputProps['onPressEnter']
  onBlur?: AntInputProps['onBlur']
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onFocus?: AntInputProps['onFocus']
  onClear?: () => void
  addonAfter?: ReactNode
  addonBefore?: ReactNode
  disabled?: boolean
  readOnly?: boolean
  allowClear?: boolean
  maxLength?: number
  min?: number | string
  max?: number | string
  step?: number
  title?: string
  border?: string
  isrequired?: boolean
  copied?: boolean
  bgcolor?: string
  color?: string
}

export const Input = forwardRef<AntInputRef, InputProps>(function Input(
  {
    type,
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    width = '100%',
    onPressEnter,
    onBlur,
    onClear,
    addonAfter,
    addonBefore,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const inputElement = (
    <AntInput
      ref={ref}
      className='glasspro-input'
      styles={{ root: { width, height } }}
      type={type}
      id={id}
      name={name ?? id}
      autoComplete={autoComplete}
      aria-label={ariaLabel ?? label ?? undefined}
      placeholder={placeholder}
      value={value ?? undefined}
      onChange={onChange}
      suffix={suffix}
      prefix={prefix}
      status={status as AntInputProps['status']}
      required={required}
      onPressEnter={onPressEnter}
      onBlur={onBlur}
      onClear={onClear}
      {...rest}
    />
  )

  const wrappedInput =
    addonAfter || addonBefore ? (
      <Space.Compact style={{ width, display: 'flex' }}>
        {addonBefore}
        {inputElement}
        {addonAfter}
      </Space.Compact>
    ) : (
      inputElement
    )

  return (
    <div className='flex flex-col gap-2 relative w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      {wrappedInput}
      {status === 'error' && errorData && (
        <RequiredCaption width={width} errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// InputNumber
// ---------------------------------------------------------------------------
export interface InputNumberFieldProps extends BaseFieldProps {
  placeholder?: string
  value?: number | string | null
  onChange?: AntInputNumberProps['onChange']
  suffix?: ReactNode
  prefix?: ReactNode
  onPressEnter?: AntInputNumberProps['onPressEnter']
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  readOnly?: boolean
}

const numberFormatter = (value: number | string | undefined) => {
  if (value === null || value === undefined || value === '') return ''
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

const numberParser = (value: string | undefined): number | null => {
  if (!value) return null
  return Number(value.replace(/\s/g, ''))
}

export const InputNumberField = forwardRef<AntInputNumberRef, InputNumberFieldProps>(
  function InputNumberField(
    {
      label,
      placeholder,
      value,
      onChange,
      height = '40px',
      suffix,
      prefix,
      status,
      required,
      errorData,
      field,
      width = '100%',
      onPressEnter,
      disabled,
      id: idProp,
      name,
      autoComplete,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) {
    const fallbackId = useId()
    const id = idProp ?? fallbackId
    return (
      <div className='flex flex-col gap-2 relative w-(width)'>
        {label && (
          <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
            {label}
            {required && <RequiredMark />}
          </label>
        )}
        <AntInputNumber
          ref={ref}
          className='glasspro-input-number'
          styles={{ root: { width, height } }}
          id={id}
          name={name ?? id}
          autoComplete={autoComplete}
          aria-label={ariaLabel ?? label ?? undefined}
          placeholder={placeholder}
          value={value !== null && value !== undefined ? Number(value) : null}
          onChange={onChange}
          disabled={disabled}
          suffix={suffix}
          prefix={prefix}
          status={status as AntInputNumberProps['status']}
          required={required}
          onPressEnter={onPressEnter}
          controls={false}
          formatter={numberFormatter}
          parser={numberParser as AntInputNumberProps['parser']}
          stringMode
          {...rest}
        />
        {status === 'error' && errorData && (
          <RequiredCaption errorData={errorData} field={field || label || ''} />
        )}
      </div>
    )
  },
)

// ---------------------------------------------------------------------------
// InputPassword
// ---------------------------------------------------------------------------
export interface InputPasswordProps extends BaseFieldProps {
  placeholder?: string
  value?: string
  onChange?: AntInputProps['onChange']
  suffix?: ReactNode
  prefix?: ReactNode
  onPressEnter?: AntInputProps['onPressEnter']
  onBlur?: AntInputProps['onBlur']
  onClear?: () => void
  disabled?: boolean
  /**
   * Determines the default `autoComplete` token: 'current-password' for
   * sign-in, 'new-password' for registration / change-password flows. Has
   * no effect if an explicit `autoComplete` prop is provided.
   */
  mode?: 'current' | 'new'
}

export const InputPassword = forwardRef<AntInputRef, InputPasswordProps>(function InputPassword(
  {
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    width = '100%',
    onPressEnter,
    onBlur,
    onClear,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    mode = 'current',
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const resolvedAutoComplete =
    autoComplete ?? (mode === 'new' ? 'new-password' : 'current-password')
  return (
    <div className='flex flex-col gap-2 relative w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
        </label>
      )}
      <AntInput.Password
        ref={ref}
        className='glasspro-input-password'
        styles={{ root: { width, height } }}
        id={id}
        name={name ?? id}
        autoComplete={resolvedAutoComplete}
        aria-label={ariaLabel ?? label ?? undefined}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        suffix={suffix}
        prefix={prefix}
        status={status as AntInputProps['status']}
        required={required}
        onPressEnter={onPressEnter}
        onBlur={onBlur}
        onClear={onClear}
        {...rest}
      />
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// CellInput (borderless input for inline table editing)
// ---------------------------------------------------------------------------
export interface CellInputProps extends BaseFieldProps {
  placeholder?: string
  value?: string
  onChange?: AntInputProps['onChange']
  suffix?: ReactNode
  prefix?: ReactNode
  onPressEnter?: AntInputProps['onPressEnter']
  onBlur?: AntInputProps['onBlur']
  onClear?: () => void
  onFocus?: AntInputProps['onFocus']
  readOnly?: boolean
  disabled?: boolean
}

export const CellInput = forwardRef<AntInputRef, CellInputProps>(function CellInput(
  {
    placeholder,
    value,
    onChange,
    height = '40px',
    suffix,
    prefix,
    status,
    errorData,
    field,
    width = '100%',
    onPressEnter,
    onBlur,
    onClear,
    onFocus,
    id: idProp,
    name,
    autoComplete = 'off',
    'aria-label': ariaLabel,
    label,
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  return (
    <div className='flex flex-col gap-2 relative w-(width)'>
      <AntInput
        ref={ref}
        id={id}
        name={name ?? id}
        autoComplete={autoComplete}
        aria-label={ariaLabel ?? label ?? field ?? undefined}
        className='glasspro-cell-input'
        styles={{ root: { width, height } }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        suffix={suffix}
        prefix={prefix}
        status={status as AntInputProps['status']}
        onPressEnter={onPressEnter}
        onBlur={onBlur}
        onClear={onClear}
        onFocus={onFocus}
        {...rest}
      />
      {status === 'error' && errorData && field && (
        <RequiredCaption errorData={errorData} field={field} />
      )}
    </div>
  )
})
